import { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getRoleNavigation, type NavigationItem } from '../config/role-navigation';
import { getRoleDisplayName, getRoleBadgeVariant } from '../utils/role-utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function navLinkClassName(isActive: boolean): string {
  return `inline-flex items-center px-1 pt-1 text-sm font-medium ${
    isActive
      ? 'text-foreground border-b-2 border-primary'
      : 'text-foreground border-b-2 border-transparent hover:border-muted'
  }`;
}

function isItemActive(item: NavigationItem, pathname: string): boolean {
  if (!item.path) return false;
  return pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path));
}

function DesktopNavItem({ item, pathname }: { item: NavigationItem; pathname: string }) {
  if (item.children && item.children.length > 0) {
    const helpActive = item.children.some((c) => c.path && pathname.startsWith(c.path));
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`${navLinkClassName(helpActive)} gap-1 outline-none`}
          >
            {item.label}
            <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[14rem]">
          {item.children.map((child) =>
            child.path ? (
              <DropdownMenuItem key={child.path} asChild>
                <Link to={child.path}>{child.label}</Link>
              </DropdownMenuItem>
            ) : null
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (!item.path) return null;

  const className = navLinkClassName(isItemActive(item, pathname));
  if (item.external) {
    return (
      <a href={item.path} className={className}>
        {item.label}
      </a>
    );
  }
  return (
    <Link to={item.path} className={className}>
      {item.label}
    </Link>
  );
}

function MobileNavItem({
  item,
  onNavigate,
}: {
  item: NavigationItem;
  onNavigate: () => void;
}) {
  const linkClass =
    'block px-3 py-2 text-base font-medium text-foreground hover:bg-muted/50 rounded-md';

  if (item.children && item.children.length > 0) {
    return (
      <div className="space-y-1">
        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {item.label}
        </div>
        {item.children.map((child) =>
          child.path ? (
            <Link
              key={child.path}
              to={child.path}
              className={`${linkClass} pl-6`}
              onClick={onNavigate}
            >
              {child.label}
            </Link>
          ) : null
        )}
      </div>
    );
  }

  if (!item.path) return null;

  if (item.external) {
    return (
      <a href={item.path} className={linkClass} onClick={onNavigate}>
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.path} className={linkClass} onClick={onNavigate}>
      {item.label}
    </Link>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigationItems = getRoleNavigation(user?.role);
  const isHelpPage = location.pathname.startsWith('/help');

  return (
    <div className={cn('min-h-screen bg-background', isHelpPage && 'flex flex-col')}>
      <nav className="bg-card shadow-sm border-b border-border shrink-0">
        <div id="nav-container" className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/" className="flex items-center px-2 py-2">
                {/* Logo for large screens */}
                <img
                  src="/images/llx-logo.svg"
                  alt="Light Logix"
                  className="hidden lg:block h-8"
                />
                {/* Bulb icon for small screens */}
                <img
                  src="/images/llx-bulb.svg"
                  alt="Light Logix"
                  className="block lg:hidden h-8"
                />
              </Link>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigationItems.map((item) => (
                  <DesktopNavItem
                    key={item.path ?? item.label}
                    item={item}
                    pathname={location.pathname}
                  />
                ))}
              </div>
            </div>
            <div className="hidden sm:flex sm:items-center">
              <div className="flex items-center gap-3 mr-4">
                {user?.role && (
                  <Badge
                    variant={getRoleBadgeVariant(user.role)}
                    className="hidden lg:inline-flex cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    {getRoleDisplayName(user.role)}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="inline-flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
            <div className="sm:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1 px-4">
              {navigationItems.map((item) => (
                <MobileNavItem
                  key={item.path ?? item.label}
                  item={item}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              ))}
            </div>
            <div className="pt-4 pb-3 border-t border-border px-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary font-medium">
                      {user?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-foreground">{user?.email}</div>
                  {user?.role && (
                    <Badge
                      variant={getRoleBadgeVariant(user.role)}
                      className="mt-1 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        navigate(`/users/${user.id}`);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {getRoleDisplayName(user.role)}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="mt-3">
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="w-full justify-start"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <main
        className={
          isHelpPage
            ? 'flex flex-1 flex-col min-h-0 w-full p-0'
            : 'max-w-8xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8'
        }
      >
        <Outlet />
      </main>
    </div>
  );
}
